import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Page from '../../common/Page'
import $t from '../../../i18n'
import ProviderSearchLink from './ProviderSearchLink'
import ProviderResourceLink from './ProviderResourceLink'
import { sortBy } from 'utils/sortUtils'
import SearchBar from './SearchBar'
import LocationSelector from './LocationSelector'
import { setUrlParams } from 'utils/useParamUtil'
import { updateProvidersSearchFromUrl } from 'utils/providerSearchUtils'
import { getResourcesForState } from 'utils/locationUtils'
import { directoryAccessParams } from 'utils/directoryAccessUtils'
import Actions from 'actions'
import { routes } from 'consts'
import qcpLogo from '../../../resources/qcp_logo.png'
import SearchBarDialog from './SearchBarDialog/SearchBarDialog'
import YearSwitcher from '../../common/YearSwitcher'
import { getUrlParams } from 'utils/useParamUtil'
import { currentYear, nextYear } from 'utils/dateUtils'
import NewProviderDirectoryDialog from '../dialogs/NewProviderDirectoryDialog/NewProviderDirectoryDialog'
import { useNativeMessage } from 'hooks'

import './SearchProviders.css'

export const SearchProviders = () => {
  const dispatch = useDispatch()
  const { isLoggedIn } = useSelector(state => state.auth)
  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState(false)
  const [query, setQuery] = useState('')

  const { hideBack } = getUrlParams()

  // Use native message hook to handle React Native WebView communication
  const { isWebViewAuthenticated } = useNativeMessage({
    sendLoadedMessage: true,
    shouldSendLoaded: hideBack
  })

  const {
    providersSearchResources,
    providersSearchEnableYearFilter,
    providersSearchLocation,
    providersSearchFilters,
    providersSupportedStates,
    providersDefaultState
  } = useSelector(state => state.providers)

  const isAuthenticated = isLoggedIn || isWebViewAuthenticated
  const userDetails = useSelector(state => state?.profile?.userDetails)
  const priceTransparencyLink = userDetails?.hmo_data?.price_transparency_link
  const resourcesForState = getResourcesForState(
    providersSearchResources,
    providersSearchLocation?.state
  )
  const popularSearches =
    sortBy(resourcesForState?.popular_search_data, 'order')?.filter(
      item => !item.hidden
    ) || []
  const externalLinks =
    sortBy(resourcesForState?.external_links, 'order')?.filter(
      item => !item.hidden
    ) || []
  // Links flagged authenticated_only are members-only, the rest are public
  const publicExternalLinks = externalLinks.filter(
    item => !item.authenticated_only
  )
  const authenticatedExternalLinks = externalLinks.filter(
    item => item.authenticated_only
  )

  const renderResourceLink = (item, condition, linkOverride) => (
    <ProviderResourceLink
      key={item.order}
      title={item.display_title}
      link={condition ? linkOverride : item.link}
      customAction={item.custom_action}
    />
  )

  const updateQuery = query => {
    dispatch(
      Actions.createAction(Actions.PROVIDERS_SEARCH_QUERY_UPDATED, query)
    )
  }

  const handleSearch = query => {
    const searchParams = {
      query,
      ...providersSearchLocation,
      ...providersSearchFilters,
      ...directoryAccessParams()
    }
    dispatch(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        `${routes.SEARCH_PROVIDERS_RESULTS}?${setUrlParams(searchParams)}`
      )
    )
  }

  useEffect(() => {
    updateProvidersSearchFromUrl(
      dispatch,
      userDetails,
      providersSupportedStates,
      providersDefaultState
    )
  }, [dispatch, userDetails, providersSupportedStates, providersDefaultState])

  const onYearChange = year => {
    dispatch(
      Actions.createAction(Actions.SEARCH_PROVIDER_FILTERS_UPDATED, {
        ...providersSearchFilters,
        year
      })
    )

    const currentParams = getUrlParams()
    const updatedParams = {
      ...currentParams,
      year
    }
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${setUrlParams(updatedParams)}`
    )
  }

  return (
    <Page
      className='search-provider-page full-height'
      title={$t('searchProviders.pageTitle')}
      fullWidth={true}
      AdditionalActionsComponent={
        providersSearchEnableYearFilter && (
          <YearSwitcher
            year={providersSearchFilters?.year}
            onYearChange={event => onYearChange(event.target.value)}
            years={[currentYear.toString(), nextYear.toString()]}
          />
        )
      }
    >
      {isAuthenticated && <NewProviderDirectoryDialog />}
      <div className='provider-search-page'>
        <p className='provider-network-subtitle'>
          {$t('searchProviders.pageSubtitle').toUpperCase()}
        </p>
        <LocationSelector />
        <SearchBar
          query={query}
          setQuery={setQuery}
          onSearch={handleSearch}
          onClickSearchBar={isMobile =>
            isMobile ? setIsMobileDialogOpen(true) : null
          }
        />
        {isMobileDialogOpen && (
          <SearchBarDialog
            query={query}
            setQuery={setQuery}
            isVisible={isMobileDialogOpen}
            onClose={() => setIsMobileDialogOpen(false)}
            onSearch={handleSearch}
          />
        )}
        <div className='provider-links-container'>
          {!!popularSearches.length && (
            <div className='provider-search-section'>
              <h3 className='provider-section-header'>
                {$t('searchProviders.popularSearches').toUpperCase()}
              </h3>
              <div className='provider-search-links-container'>
                {popularSearches.map(item => (
                  <ProviderSearchLink
                    key={item.order}
                    title={item.display_title}
                    image={item.image_name}
                    onClick={() => {
                      updateQuery(item?.search_term)
                      handleSearch(item?.search_term)
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          {(!!publicExternalLinks.length ||
            !!authenticatedExternalLinks.length) && (
            <div className='provider-resource-section'>
              <h3 className='provider-section-header'>
                {$t('searchProviders.additionalResources').toUpperCase()}
              </h3>
              <div className='provider-resource-links-container'>
                {publicExternalLinks.map(renderResourceLink)}
                {isAuthenticated &&
                  authenticatedExternalLinks.map(item =>
                    renderResourceLink(
                      item,
                      item.link === 'price-transparency',
                      priceTransparencyLink
                    )
                  )}
              </div>
            </div>
          )}
        </div>
        {providersSearchLocation?.state === 'OH' && (
          <div className='oh-qcp-footer'>
            {$t('searchProviders.ohQcpFooter')} <img src={qcpLogo} alt='' />
          </div>
        )}
      </div>
    </Page>
  )
}

export default SearchProviders
